/**
 * Mix & Match on product cards.
 *
 * Makes the Mix & Match popup — until now PDP-only — open when a variant is
 * added to cart from ANY product card (Algolia collection/search cards and the
 * Liquid default cards on the home page, product recommendations, etc.).
 *
 * The popup itself is rendered on demand by sections/mix-and-match-popup.liquid
 * via the Section Rendering API (one request per added product; M&M is <= 20
 * products so no batching). The injected <custom-dialog> reuses the exact same
 * element/behaviour as the PDP popup (assets/0-product-content.js): open/close,
 * drawer-vs-centered, sort, ATC limit; adds from inside it are handled by the
 * existing ProductForm flow (mark-in-cart + close-when-empty). Here we only:
 *   1) fetch + inject the popup for the added product,
 *   2) preselect each card's variant to match the added variant's size
 *      (reusing window.ProductsByHandle.matchVariantBySize — same levels as the
 *      PDP preselectCtlVariants, incl. the level-4 first-available fallback),
 *   3) fill the "added to cart" summary and open it in ATC (centered) mode.
 *
 * The add-to-cart handlers (js--all-dawn.js ProductForm + algolia-product-
 * helpers.js) call window.MixAndMatchCards.isSurfaceEnabled() / openForAdd().
 */
(function () {
  'use strict';

  // Flip to true to trace the fetch → inject → preselect → open flow in the console.
  const DEBUG = false;
  const LOG = DEBUG ? (...a) => console.log('[mm-cards]', ...a) : () => {};
  const root = () => ((window.Shopify && Shopify.routes && Shopify.routes.root) || '/');

  let config = null;
  function getConfig() {
    if (config) return config;
    config = { collection: false, search: false, other: false };
    try {
      const el = document.getElementById('mm-card-config');
      if (el) Object.assign(config, JSON.parse(el.textContent || '{}'));
      else LOG('getConfig: #mm-card-config NOT found in DOM');
    } catch (e) { LOG('getConfig: parse error', e); }
    LOG('config =', config);
    return config;
  }

  function currentSurface() {
    const b = document.body.classList;
    if (b.contains('template-collection')) return 'collection';
    if (b.contains('template-search')) return 'search';
    return 'other';
  }

  function isSurfaceEnabled() {
    // Called from the critical add-to-cart path (ProductForm + Algolia) — must
    // never throw. Any failure = feature simply off for this add.
    try {
      const surface = currentSurface();
      const enabled = !!getConfig()[surface];
      LOG('isSurfaceEnabled: surface =', surface, '→', enabled);
      return enabled;
    } catch (e) {
      LOG('isSurfaceEnabled: error (treating as disabled)', e);
      return false;
    }
  }

  function ensureHost() {
    let host = document.getElementById('mm-card-popup-host');
    if (!host) {
      host = document.createElement('div');
      host.id = 'mm-card-popup-host';
      document.body.appendChild(host);
    }
    return host;
  }

  function normalizeItem(addedItem) {
    const item = addedItem && addedItem.items ? addedItem.items[0] : addedItem;
    if (!item) return null;
    return {
      handle: item.product_handle || item.handle || null,
      variantId: item.variant_id || item.id || null,
      variantTitle: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '',
      productTitle: item.product_title || item.title || '',
    };
  }

  // Preselect each M&M card's <select> to the added variant's size — the same
  // atcMatch/defaultMatch logic as the PDP preselectCtlVariants, but scoped to
  // this dialog and seeded from the popup's embedded added-variant context.
  function preselect(dialog) {
    const api = window.ProductsByHandle;
    if (!api || !api.matchVariantBySize) return;

    let grouping = [];
    try { grouping = JSON.parse(dialog.dataset.mmAddedGrouping || '[]') || []; } catch (e) { grouping = []; }
    const source = { grouping: grouping, title: dialog.dataset.mmAddedVariantTitle || '' };

    dialog.querySelectorAll('.product-card--mm').forEach((form) => {
      const jsonEl = form.querySelector('.product-variants-data-json');
      if (!jsonEl) return;
      let variants;
      try { variants = JSON.parse(jsonEl.textContent).variants; } catch (e) { return; }

      const atc = api.matchVariantBySize(variants, source, { availableOnly: true });
      const def = api.matchVariantBySize(variants, source, { allowFirstAvailableFallback: true });
      form.classList.toggle('product-card--mm-atc-hidden', !atc);

      const preferred = atc || def;
      const select = form.querySelector('select.product-variant-id');
      if (select && preferred) {
        const opt = [...select.options].find((o) => Number(o.value) === preferred.id);
        if (opt) select.value = String(preferred.id);
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function fillSummary(dialog, item) {
    const titleEl = dialog.querySelector('.ctl-new__atc-product-title');
    const sizeEl = dialog.querySelector('.ctl-new__atc-product-size');
    const sizeWrap = dialog.querySelector('.ctl-new__atc-product-size-wrap');
    if (titleEl) titleEl.textContent = item.productTitle;
    if (sizeEl) sizeEl.textContent = item.variantTitle;
    if (sizeWrap) sizeWrap.classList.toggle('hidden', !item.variantTitle);
  }

  // Returns true if the popup was opened (caller then suppresses the cart drawer).
  async function openForAdd(addedItem) {
    LOG('openForAdd: raw addedItem =', addedItem);
    const item = normalizeItem(addedItem);
    LOG('openForAdd: normalized item =', item);
    if (!item || !item.handle || !item.variantId) { LOG('openForAdd: BAIL — missing handle/variantId'); return false; }

    let html = '';
    let dialogNode;
    try {
      const q = encodeURIComponent(item.handle + ',' + item.variantId);
      const url = root() + 'search?section_id=mix-and-match-popup&q=' + q;
      LOG('openForAdd: fetching', url);
      const res = await fetch(url);
      LOG('openForAdd: response status', res.status, 'ok', res.ok);
      if (!res.ok) { LOG('openForAdd: BAIL — response not ok'); return false; }
      html = await res.text();
      LOG('openForAdd: response length', html.length);
      const doc = new DOMParser().parseFromString(html, 'text/html');
      dialogNode = doc.querySelector('custom-dialog.ctl-new__dialog');
    } catch (e) {
      LOG('openForAdd: fetch threw', e);
      return false;
    }
    if (!dialogNode) {
      LOG('openForAdd: BAIL — no <custom-dialog.ctl-new__dialog> in response. First 300 chars:', html.slice(0, 300));
      return false;
    }
    LOG('openForAdd: dialog found; card count in response =', dialogNode.querySelectorAll('.product-card--mm').length);

    const host = ensureHost();
    // Everything from here (inject → preselect → open) is wrapped so a missing
    // element, a renamed class, or an un-upgraded dialog can never throw into the
    // add-to-cart flow — we just bail (return false) and the caller opens the cart.
    try {
      host.innerHTML = '';
      host.appendChild(document.importNode(dialogNode, true));
      const dialog = host.querySelector('custom-dialog.ctl-new__dialog');
      if (!dialog) { LOG('openForAdd: BAIL — dialog missing after inject'); host.innerHTML = ''; return false; }

      preselect(dialog);
      fillSummary(dialog, item);

      // No eligible (in-stock, size-matching, within price cap) card → bail so the
      // caller opens the cart drawer instead.
      const hasMatch = dialog.querySelector(
        '.product-card--mm:not(.product-card--mm-atc-hidden):not(.product-card--mm-atc-price-excluded)'
      );
      if (!hasMatch) { LOG('openForAdd: BAIL — no eligible card after preselect (all hidden/price-excluded)'); host.innerHTML = ''; return false; }

      if (typeof dialog.openModal !== 'function') { LOG('openForAdd: BAIL — dialog.openModal not a function (custom-dialog not upgraded?)'); host.innerHTML = ''; return false; }

      // Close the cart drawer first (both are native <dialog>s in the top layer).
      document.querySelector('cart-drawer')?.close?.();
      LOG('openForAdd: opening ATC modal');
      dialog.openModal({ mode: 'atc' });
      return true;
    } catch (e) {
      LOG('openForAdd: post-fetch error (bailing to cart drawer)', e);
      try { host.innerHTML = ''; } catch (_) { /* ignore */ }
      return false;
    }
  }

  window.MixAndMatchCards = { isSurfaceEnabled: isSurfaceEnabled, openForAdd: openForAdd, currentSurface: currentSurface };
  LOG('loaded. ProductsByHandle present:', !!(window.ProductsByHandle && window.ProductsByHandle.matchVariantBySize));
})();
